export type WalletSpendLedgerEntry = {
  id?: string;
  transaction_id?: string;
  entry_type?: string;
  debit_micros?: number;
  credit_micros?: number;
  reference_type?: string;
  reference_id?: string;
  metadata_json?: string;
  created_at?: string;
};

export type WalletSpendChartPoint = { d: string; v: number };

const MICRO = 1_000_000;

function stringValue(value: unknown) {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value).trim();
  return "";
}

function lowerValue(value: unknown) {
  return stringValue(value).toLowerCase();
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function firstString(...values: unknown[]) {
  for (const value of values) {
    const text = stringValue(value);
    if (text) return text;
  }
  return "";
}

function ledgerEntryMetadata(entry: WalletSpendLedgerEntry) {
  const raw = stringValue(entry.metadata_json);
  if (!raw) return {};
  try {
    return asRecord(JSON.parse(raw));
  } catch {
    return {};
  }
}

function ledgerSpendGroupKey(entry: WalletSpendLedgerEntry) {
  const referenceType = lowerValue(entry.reference_type);
  const referenceID = stringValue(entry.reference_id);
  if (referenceType === "wallet_credit_reservation" && referenceID) return `reservation:${referenceID}`;
  if (["provider_job", "wallet_provider_job", "genrl_provider_job"].includes(referenceType) && referenceID) return `job:${referenceID}`;
  const metadata = ledgerEntryMetadata(entry);
  const jobID = firstString(
    metadata.provider_job_id,
    metadata.job_id,
    metadata.genrl_job_id,
    metadata.client_launch_id,
    metadata.runtime_session_id,
  );
  if (jobID) return `job:${jobID}`;
  return `entry:${firstString(
    entry.id,
    entry.transaction_id,
    referenceType && referenceID ? `${referenceType}:${referenceID}` : "",
    `${entry.created_at || ""}:${entry.entry_type || ""}:${entry.debit_micros || 0}:${entry.credit_micros || 0}`,
  )}`;
}

function ledgerEntryDebitMicros(entry: WalletSpendLedgerEntry) {
  const micros = Number(entry.debit_micros || 0);
  return Number.isFinite(micros) ? Math.max(0, micros) : 0;
}

function ledgerEntryCreditMicros(entry: WalletSpendLedgerEntry) {
  const micros = Number(entry.credit_micros || 0);
  return Number.isFinite(micros) ? Math.max(0, micros) : 0;
}

export function walletSpendChart(entries: WalletSpendLedgerEntry[] | null | undefined, now = new Date()): WalletSpendChartPoint[] {
  const days: WalletSpendChartPoint[] = [];
  const byDay = new Map<string, number>();
  type SpendGroup = {
    latestMs: number;
    netMicros: number;
    reservationDebitMicros: number;
    reservationReleaseMicros: number;
    finalReservationCostMicros: number;
    sawReservationDebit: boolean;
    sawReservationRelease: boolean;
    sawFinalReservationCost: boolean;
  };
  const groups = new Map<string, SpendGroup>();
  for (const entry of Array.isArray(entries) ? entries : []) {
    const date = new Date(String(entry.created_at || ""));
    if (Number.isNaN(date.getTime())) continue;
    const key = ledgerSpendGroupKey(entry);
    const group = groups.get(key) || {
      latestMs: date.getTime(),
      netMicros: 0,
      reservationDebitMicros: 0,
      reservationReleaseMicros: 0,
      finalReservationCostMicros: 0,
      sawReservationDebit: false,
      sawReservationRelease: false,
      sawFinalReservationCost: false,
    };
    group.latestMs = Math.max(group.latestMs, date.getTime());
    const debitMicros = ledgerEntryDebitMicros(entry);
    const creditMicros = ledgerEntryCreditMicros(entry);
    const entryType = lowerValue(entry.entry_type);
    const reservationEntry = lowerValue(entry.reference_type) === "wallet_credit_reservation";
    if (reservationEntry && entryType.includes("job_cost")) {
      group.finalReservationCostMicros += debitMicros - creditMicros;
      group.sawFinalReservationCost = true;
    } else if (reservationEntry && entryType.includes("reserve")) {
      group.reservationDebitMicros += debitMicros;
      group.sawReservationDebit = true;
    } else if (reservationEntry && entryType.includes("release")) {
      group.reservationReleaseMicros += creditMicros;
      group.sawReservationRelease = true;
    } else {
      group.netMicros += debitMicros - creditMicros;
    }
    groups.set(key, group);
  }
  for (const group of groups.values()) {
    let spendMicros = group.netMicros;
    if (group.sawFinalReservationCost) {
      spendMicros += group.finalReservationCostMicros;
    } else if (group.sawReservationRelease) {
      spendMicros += group.reservationDebitMicros - group.reservationReleaseMicros;
    } else if (group.sawReservationDebit) {
      spendMicros += 0;
    }
    if (spendMicros <= 0) continue;
    const date = new Date(group.latestMs);
    const key = date.toISOString().slice(0, 10);
    byDay.set(key, (byDay.get(key) || 0) + spendMicros / MICRO);
  }
  for (let i = 29; i >= 0; i -= 1) {
    const date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - i));
    const key = date.toISOString().slice(0, 10);
    days.push({
      d: date.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      v: Number((byDay.get(key) || 0).toFixed(2)),
    });
  }
  return days;
}

export function walletSpendDisplayValue(value: number) {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount) || amount <= 0) return "0";
  if (amount < 1) return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return amount.toLocaleString(undefined, { maximumFractionDigits: amount < 10 ? 2 : 0 });
}

export function walletChartRangeLabel(points: WalletSpendChartPoint[] | null | undefined) {
  const labels = Array.isArray(points) ? points.map((point) => String(point.d || "").trim()).filter(Boolean) : [];
  if (labels.length === 0) return "Last 30 days";
  return `${labels[0]} – ${labels[labels.length - 1]}`;
}
